// SignIn.tsx - Production Ready (Working Google Auth + localStorage cache)

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  signInWithEmailAndPassword, 
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../../lib/firebase";
import { supabase } from "../../lib/supabaseClient";
import logo from '../../assets/images/logo.png';

const SignIn: React.FC = () => {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [loginMethod, setLoginMethod] = useState<'google' | 'phone' | null>(null);
  const [phoneOrEmail, setPhoneOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appDownloading, setAppDownloading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // ============================================================
  // SET AUTH PERSISTENCE (like practice project)
  // ============================================================
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log("✅ Firebase Auth persistence set to LOCAL");
      })
      .catch((error) => {
        console.error("⚠️ Error setting persistence:", error);
      });
  }, []);

  // ============================================================
  // AUTO-FILL FROM LOCALSTORAGE (fast next-time login)
  // ============================================================
  useEffect(() => {
    try {
      const cached = localStorage.getItem('gostorez_user_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.identifier) setPhoneOrEmail(parsed.identifier);
        if (parsed.name) console.log('Welcome back,', parsed.name);
      }
    } catch (err) {
      console.warn('Failed to read user cache:', err);
    }
  }, []);

  // ============================================================
  // AUTH STATE LISTENER (same as practice project)
  // ============================================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('✅ Auth state: user signed in', user.email);
        navigate(from, { replace: true });
      } else {
        console.log('Auth state: no user');
      }
    });
    return () => unsubscribe();
  }, [navigate, from]);

  // ============================================================
  // CACHE USER DATA (for fast next-time login)
  // ============================================================
  const cacheUserData = (userData: { uid: string; identifier: string; name: string; tier?: string }) => {
    try {
      localStorage.setItem('gostorez_user_cache', JSON.stringify({
        ...userData,
        lastLogin: new Date().toISOString()
      }));
      localStorage.setItem('userIdentifier', userData.identifier);
    } catch (err) {
      console.warn('Failed to cache user data:', err);
    }
  };

  // ============================================================
  // APP DOWNLOAD
  // ============================================================
  const handleDownloadApp = async () => {
    if (appDownloading) return;
    setAppDownloading(true);
    setError("");

    try {
      let downloadUrl = "";

      const { data, error } = await supabase
        .from('apps')
        .select('direct_link')
        .eq('app_name', 'Gostorez')
        .limit(1);

      if (!error && data && data.length > 0 && data[0].direct_link) {
        downloadUrl = data[0].direct_link;
      }

      if (!downloadUrl) {
        downloadUrl = "https://github.com/israel0132/NewP/raw/main/Gostorez.apk";
      }

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "Gostorez.apk";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error("Download error:", err);
      setError("Failed to download app. Please try again.");
    } finally {
      setAppDownloading(false);
    }
  };

  // ============================================================
  // GOOGLE SIGN IN (exact strategy from working practice project)
  // ============================================================
  const handleGoogleSignIn = async () => {
    setError("");
    setSuccess("");
    setGoogleLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Ensure persistence BEFORE sign-in (critical for mobile)
      await setPersistence(auth, browserLocalPersistence);

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log('✅ Google sign-in success:', user.email);

      // Check if user exists in Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      let userName = user.displayName || "User";
      let userTier = "free";

      if (!userDoc.exists()) {
        // New user — create profile
        await setDoc(userDocRef, {
          name: userName,
          email: user.email || "",
          phone: user.phoneNumber || "",
          profileImage: user.photoURL || "",
          role: "user",
          tier: "free",
          isVerified: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      } else {
        const existingData = userDoc.data();
        userName = existingData.name || userName;
        userTier = existingData.tier || "free";
      }

      // Cache for fast next-time login
      cacheUserData({
        uid: user.uid,
        identifier: user.email || user.phoneNumber || '',
        name: userName,
        tier: userTier
      });

      // Navigate home
      navigate(from, { replace: true });

    } catch (error: any) {
      console.error('Google signin error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups for this site.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google sign-in. Contact support.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Sign in was cancelled');
      } else {
        setError(error.message || "Failed to sign in with Google");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const detectInputType = (input: string): 'phone' | 'email' => {
    const cleanInput = input.replace(/[\s\-()+]/g, '');
    if (/^\d+$/.test(cleanInput) && cleanInput.length >= 10) {
      return 'phone';
    }
    if (input.includes('@') && input.includes('.')) {
      return 'email';
    }
    if (/\d/.test(input)) {
      return 'phone';
    }
    return 'email';
  };

  const isNetworkOrUnknownError = (code: string): boolean => {
    const networkCodes = [
      'auth/network-request-failed',
      'auth/internal-error',
      'auth/timeout',
      'auth/operation-not-allowed',
      'auth/too-many-requests',
      'auth/quota-exceeded',
    ];
    return networkCodes.includes(code);
  };

  // ============================================================
  // FORGOT PASSWORD
  // ============================================================
  const handleForgotPassword = async () => {
    setError("");
    setSuccess("");

    const cleanIdentifier = phoneOrEmail.trim();
    if (!cleanIdentifier) {
      setError("Please enter your email address first");
      return;
    }

    const inputType = detectInputType(cleanIdentifier);
    if (inputType === 'phone') {
      setError("Password reset is only available for email accounts. Phone users should sign up again if they forgot their PIN.");
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, cleanIdentifier);
      setSuccess("✅ Password reset link sent! Check your email inbox.");
    } catch (err: any) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError("No account found with this email address");
      } else if (err.code === 'auth/invalid-email') {
        setError("Please enter a valid email address");
      } else if (err.code === 'auth/too-many-requests') {
        setError("Too many requests. Please try again later.");
      } else {
        setError(err.message || "Failed to send reset email");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const inputType = detectInputType(phoneOrEmail);
    const cleanIdentifier = phoneOrEmail.trim();

    if (!cleanIdentifier) {
      setError("Please enter your phone number or email");
      return;
    }

    if (inputType === 'phone') {
      const cleanPhone = cleanIdentifier.replace(/[\s\-()+]/g, '');
      if (cleanPhone.length < 10) {
        setError("Please enter a valid phone number");
        return;
      }
    }

    let emailForAuth = cleanIdentifier;
    let phone = '';
    if (inputType === 'phone') {
      const cleanPhone = cleanIdentifier.replace(/[\s\-()+]/g, '');
      phone = cleanPhone;
      emailForAuth = `${cleanPhone}@campusgostorez.local`;
    }

    // ============================================================
    // SIGN UP FLOW
    // ============================================================
    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError("Passwords don't match");
        return;
      }
      
      if (inputType === 'phone') {
        if (password.length !== 6 || !/^\d{6}$/.test(password)) {
          setError("Password must be exactly 6 digits (0-9)");
          return;
        }
      } else {
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }
      }

      setLoading(true);
      try {
        // Ensure persistence
        await setPersistence(auth, browserLocalPersistence);

        const { user } = await createUserWithEmailAndPassword(
          auth,
          emailForAuth,
          password
        );

        const displayName = inputType === 'phone' ? `User ${phone}` : cleanIdentifier.split('@')[0];

        await updateProfile(user, { displayName });

        await setDoc(doc(db, "users", user.uid), {
          name: displayName,
          email: emailForAuth,
          phone: phone || null,
          profileImage: "",
          role: "user",
          tier: "free",
          isVerified: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });

        // Cache for fast next-time login
        cacheUserData({
          uid: user.uid,
          identifier: cleanIdentifier,
          name: displayName,
          tier: 'free'
        });

        navigate(from, { replace: true });

      } catch (error: any) {
        console.error('Signup error:', error);
        if (error.code === 'auth/email-already-in-use') {
          setError("This account already exists. Please sign in.");
          setMode('signin');
        } else if (error.code === 'auth/weak-password') {
          setError("Password is too weak. Please use a stronger password.");
        } else if (error.code === 'auth/network-request-failed') {
          setError("Network error. Please check your internet connection.");
        } else {
          setError(error.message || "Failed to create account");
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // ============================================================
    // SIGN IN FLOW
    // ============================================================
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);

      await signInWithEmailAndPassword(auth, emailForAuth, password);
      
      // Cache for fast next-time login
      cacheUserData({
        uid: '',
        identifier: cleanIdentifier,
        name: cleanIdentifier.split('@')[0],
        tier: 'free'
      });

      navigate(from, { replace: true });

    } catch (error: any) {
      console.error('Signin error:', error);
      const code = error?.code || '';

      const isCredentialError = 
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found';

      if (isCredentialError) {
        if (code === 'auth/wrong-password') {
          setError("❌ Incorrect password. Please try again.");
          setLoading(false);
          return;
        }

        let accountExists = false;
        try {
          const methods = await fetchSignInMethodsForEmail(auth, emailForAuth);
          accountExists = methods && methods.length > 0;
        } catch (probeError: any) {
          if (isNetworkOrUnknownError(probeError?.code || '')) {
            setError("Network error. Please check your connection and try again.");
            setLoading(false);
            return;
          }
          accountExists = false;
        }

        if (accountExists) {
          setError("❌ Incorrect password. Please try again.");
        } else {
          setError("Incorrect credentials or account not found. Please try again or create an account first.");
          setMode('signup');
          setPassword("");
          setConfirmPassword("");
        }
        setLoading(false);
        return;
      }

      if (code === 'auth/network-request-failed') {
        setError("Network error. Please check your internet connection.");
      } else if (code === 'auth/too-many-requests') {
        setError("Too many failed attempts. Please try again later.");
      } else if (code === 'auth/user-disabled') {
        setError("This account has been disabled.");
      } else {
        setError(error.message || "Failed to sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmPassword("");
  };

  const selectMethod = (method: 'google' | 'phone') => {
    setLoginMethod(method);
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmPassword("");
  };

  const goBackToMethodSelection = () => {
    setLoginMethod(null);
    setError("");
    setSuccess("");
    setPassword("");
    setConfirmPassword("");
  };

  const getInputPlaceholder = () => "Enter phone number or email";

  const getPasswordPlaceholder = () => {
    const inputType = detectInputType(phoneOrEmail);
    if (mode === 'signup') {
      return inputType === 'phone' ? "Create 6-digit PIN" : "Create password";
    }
    return "Enter your password";
  };

  return (
    <div className="min-h-screen bg-[#F4F1E8] flex flex-col justify-center py-6 sm:px-6 lg:px-8 relative">
      {/* Get App Button */}
      <button
        onClick={handleDownloadApp}
        disabled={appDownloading}
        className="fixed top-4 right-4 z-50 bg-[#9B4819] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#7a3914] transition duration-150 shadow-lg flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {appDownloading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Downloading...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Get App
          </>
        )}
      </button>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src={logo} alt="Campus GOSTOREz" className="h-24 w-auto" />
        </div>
        <h2 className="mt-4 text-center text-2xl font-bold text-[#9B4819]">
          {mode === 'signin' ? 'Welcome Back' : 'Create Account'}
        </h2>
        <p className="mt-1 text-center text-sm text-gray-600">
          {mode === 'signin' 
            ? 'Sign in to continue to Campus GOSTOREz' 
            : 'Join Campus GOSTOREz in seconds'}
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 px-6 shadow-xl rounded-lg sm:px-8 border border-gray-100">
          
          {!loginMethod && (
            <div className="space-y-3">
              <button
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {googleLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                    Connecting...
                  </div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                onClick={() => selectMethod('phone')}
                className="w-full flex items-center justify-center px-4 py-3 border border-[#9B4819] rounded-md shadow-sm text-sm font-medium text-white bg-[#9B4819] hover:bg-[#7a3914] transition duration-150"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Continue with Phone
              </button>

              {mode === 'signin' ? (
                <p className="text-center text-sm text-gray-600 mt-3">
                  Don't have an account?{" "}
                  <button
                    onClick={() => { setMode('signup'); setLoginMethod(null); }}
                    className="font-medium text-[#9B4819] hover:text-[#7a3914]"
                  >
                    Sign up
                  </button>
                </p>
              ) : (
                <p className="text-center text-sm text-gray-600 mt-3">
                  Already have an account?{" "}
                  <button
                    onClick={() => { setMode('signin'); setLoginMethod(null); }}
                    className="font-medium text-[#9B4819] hover:text-[#7a3914]"
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>
          )}

          {loginMethod === 'phone' && (
            <form onSubmit={handlePhoneSubmit} className="space-y-5">
              <button
                type="button"
                onClick={goBackToMethodSelection}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number or Email
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    value={phoneOrEmail}
                    onChange={(e) => setPhoneOrEmail(e.target.value)}
                    placeholder={getInputPlaceholder()}
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-[#9B4819] focus:border-[#9B4819] text-base sm:text-sm"
                    required
                  />
                  {phoneOrEmail && (
                    <p className="mt-1 text-xs text-gray-500">
                      {detectInputType(phoneOrEmail) === 'phone' ? '📱 Phone number' : '✉️ Email'}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {mode === 'signin' ? 'Password' : 'Create Password'}
                  </label>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetLoading}
                      className="text-xs text-[#9B4819] hover:text-[#7a3914] font-medium transition disabled:opacity-50"
                    >
                      {resetLoading ? 'Sending...' : 'Forgot password?'}
                    </button>
                  )}
                </div>
                <div className="mt-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={getPasswordPlaceholder()}
                    className="appearance-none block w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-[#9B4819] focus:border-[#9B4819] text-base sm:text-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
                {mode === 'signup' && detectInputType(phoneOrEmail) === 'phone' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a 6-digit PIN (e.g., 123456)
                  </p>
                )}
              </div>

              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Repeat Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        const val = e.target.value;
                        const inputType = detectInputType(phoneOrEmail);
                        if (inputType === 'phone') {
                          if (val.length <= 6 && /^\d*$/.test(val)) {
                            setConfirmPassword(val);
                          }
                        } else {
                          setConfirmPassword(val);
                        }
                      }}
                      placeholder="Repeat your password"
                      className="appearance-none block w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-[#9B4819] focus:border-[#9B4819] text-base sm:text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">⚠️ Passwords don't match</p>
                  )}
                  {password && confirmPassword && password === confirmPassword && (
                    <p className="mt-1 text-xs text-green-500">✅ Passwords match</p>
                  )}
                </div>
              )}

              {error && (
                <div className="rounded-md p-3 bg-red-50 border border-red-200">
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {success && (
                <div className="rounded-md p-3 bg-green-50 border border-green-200">
                  <span className="text-sm text-green-700">{success}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (mode === 'signup' && password !== confirmPassword)}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#9B4819] hover:bg-[#7a3914] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9B4819] transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {mode === 'signin' ? 'Signing in...' : 'Creating account...'}
                  </div>
                ) : (
                  mode === 'signin' ? 'Sign In' : 'Create Account'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-sm text-[#9B4819] hover:text-[#7a3914]"
                >
                  {mode === 'signin' 
                    ? "Don't have an account? Sign up" 
                    : "Already have an account? Sign in"}
                </button>
              </div>

              {mode === 'signup' && (
                <p className="text-center text-xs text-gray-500 pt-1">
                  By creating an account, you agree to our{" "}
                  <Link to="/terms" className="text-[#9B4819] hover:text-[#7a3914] font-medium underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[#9B4819] hover:text-[#7a3914] font-medium underline">
                    Privacy Policy
                  </Link>
                </p>
              )}
            </form>
          )}
        </div>

        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
            <Link to="/terms" className="hover:text-[#9B4819] transition">Terms</Link>
            <span className="text-gray-300">•</span>
            <Link to="/privacy" className="hover:text-[#9B4819] transition">Privacy</Link>
            <span className="text-gray-300">•</span>
            <a
              href="https://wa.me/2349134081226?text=need%20assistance%20navigating%20gostorez%20pages"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#9B4819] transition"
            >
              Help
            </a>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            © {new Date().getFullYear()} Campus GOSTOREz
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;